/* Load native tag-filtered blog feeds so older matching posts are included. */
(() => {
  if (customElements.get('blog-posts-filter')) return;
  const feeds = new Map();

  function loadFeed(url) {
    if (!feeds.has(url)) {
      feeds.set(url, fetch(url, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) throw new Error('Unable to load blog posts');
          return response.text();
        })
        .then(html => {
          const feed = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-filter-feed]');
          if (!feed) throw new Error('Missing blog post feed');
          return Array.from(feed.querySelectorAll('[data-filter-card]'));
        })
        .catch(error => {
          feeds.delete(url);
          throw error;
        }));
    }
    return feeds.get(url);
  }

  class BlogPostsFilter extends HTMLElement {
    connectedCallback() {
      // Deferring also covers custom elements inserted by the theme editor.
      queueMicrotask(() => this.loadPosts());
    }

    async loadPosts() {
      const list = this.querySelector('[data-filter-list]');
      const tags = JSON.parse(this.dataset.tags || '[]');
      if (!list || !tags.length || !this.dataset.blogUrl) return;
      const options = JSON.parse(this.dataset.options || '{}');
      list.setAttribute('aria-busy', 'true');
      try {
        const results = await Promise.all(tags.map(tag => {
          const url = new URL(`${this.dataset.blogUrl}/tagged/${encodeURIComponent(tag)}`, location.origin);
          url.searchParams.set('view', 'filtered-post-cards');
          return loadFeed(url.href);
        }));
        if (!this.isConnected) return;
        const unique = new Map();
        results.flat().forEach(card => {
          if (card.dataset.articleId !== this.dataset.currentId) unique.set(card.dataset.articleId, card);
        });
        const cards = Array.from(unique.values())
          .sort((a, b) => Number(b.dataset.published) - Number(a.dataset.published) || b.dataset.articleId.localeCompare(a.dataset.articleId))
          .slice(0, Number(this.dataset.limit));
        const fragment = document.createDocumentFragment();
        cards.forEach((source, index) => {
          const card = source.cloneNode(true);
          card.className = `article grid__item ${list.dataset.grid}`;
          card.dataset.slide = index;
          card.dataset.slideIndex = index;
          const image = card.querySelector('.article__image');
          if (image) {
            image.style.height = 'auto';
            image.style.paddingTop = `${Number(options.aspect_ratio || 1) * 100}%`;
          }
          // Async images must not wait for the theme's initial animation scan.
          card.querySelectorAll('[data-aos]').forEach(element => {
            Array.from(element.attributes).forEach(attribute => {
              if (attribute.name.startsWith('data-aos')) element.removeAttribute(attribute.name);
            });
          });
          for (const [selector, enabled] of [
            ['[data-card-tags]', options.show_tags],
            ['[data-card-date]', options.show_date],
            ['[data-card-author]', options.show_author],
            ['[data-card-comments]', options.show_comments],
            ['[data-card-button]', options.show_button]
          ]) {
            if (!enabled) card.querySelectorAll(selector).forEach(element => element.remove());
          }
          if (!options.show_date && !options.show_author && !options.show_comments) {
            card.querySelectorAll('.article__meta').forEach(element => element.remove());
          }
          fragment.appendChild(card);
        });
        list.replaceChildren(fragment);
        const status = this.querySelector('[data-filter-status]');
        if (status) status.hidden = cards.length > 0;
        if (window.lazySizes) window.lazySizes.loader.checkElems();
      } catch (error) {
        // Keep server-rendered matching posts if the request fails. Never show unfiltered posts.
        const status = this.querySelector('[data-filter-status]');
        if (status && !list.querySelector('[data-filter-card]')) {
          status.hidden = false;
          status.textContent = 'Could not load matching posts. Check the tag names and reload the preview.';
        }
      } finally {
        list.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('blog-posts-filter', BlogPostsFilter);
})();
